import axiosInstance from './axiosInstance';

/**
 * Menus Service
 * Handles all menu related API calls
 */

const menusService = {
  /**
   * Helper function to safely parse menu IDs from stored user data
   * @param {any} ids 
   * @returns {Array<number>}
   */
  parseMenuIds: (ids) => {
    if (!ids) return [];
    if (typeof ids === 'string') {
      try {
        const parsed = JSON.parse(ids);
        return Array.isArray(parsed) ? parsed.map(Number) : [];
      } catch (e) {
        const num = Number(ids);
        return isNaN(num) ? [] : [num];
      }
    }
    return Array.isArray(ids) ? ids.map(Number) : [];
  },

  /**
   * Get all accessible menus for the current user
   * @returns {Promise}
   */
  getUserMenus: async () => {
    try {
      const response = await axiosInstance.get('/menus');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Check if user has access to a specific menu
   * @param {number} menuId
   * @returns {boolean}
   */
  hasMenuAccess: (menuId) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const menuIds = this.parseMenuIds(user.menuIds);
      return menuIds.includes(Number(menuId));
    } catch (error) {
      console.error('Error checking menu access:', error);
      return false;
    }
  },

  /**
   * Get all accessible menus from stored user data (active menus only)
   * @returns {Array}
   */
  getAccessibleMenus: () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user.menus || [];
    } catch (error) {
      console.error('Error getting accessible menus:', error);
      return [];
    }
  },

  /**
   * Get all available menus (for sidebar display - includes inactive ones)
   * @returns {Array}
   */
  getAllAvailableMenus: () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user.allMenus || [];
    } catch (error) {
      console.error('Error getting all available menus:', error);
      return [];
    }
  },

  /**
   * Get user's accessible menu IDs
   * @returns {Array}
   */
  getMenuIds: () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return this.parseMenuIds(user.menuIds);
    } catch (error) {
      console.error('Error getting menu IDs:', error);
      return [];
    }
  },

  /**
   * Get menu by code from all available menus
   * @param {string} code
   * @returns {Object|null}
   */
  getMenuByCode: (code) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const allMenus = user.allMenus || [];
      return allMenus.find((menu) => menu.code === code) || null;
    } catch (error) {
      console.error('Error getting menu by code:', error);
      return null;
    }
  },

  /**
   * Get menu by ID from all available menus
   * @param {number} menuId
   * @returns {Object|null}
   */
  getMenuById: (menuId) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const allMenus = user.allMenus || [];
      return allMenus.find((menu) => menu.id === menuId) || null;
    } catch (error) {
      console.error('Error getting menu by ID:', error);
      return null;
    }
  },

  /**
   * Check if user is admin
   * @returns {boolean}
   */
  isAdmin: () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const roleCode = user.role?.role_code;
      return roleCode === 'admin' || roleCode === 'super_admin';
    } catch (error) {
      console.error('Error checking admin status:', error);
      return false;
    }
  },
};

export default menusService;
